import { type LoaderFunctionArgs, json } from '@remix-run/node'
import { useLoaderData } from '@remix-run/react'
import { Flex } from '#app/components/layout/flex.tsx'
import { H2 } from '#app/components/typography/h2.tsx'
import { P } from '#app/components/typography/p.tsx'
import { Code } from '#app/old_components/typography/code.tsx'
import { sessionKey } from '#app/utils/auth.server.ts'
import { prisma } from '#app/utils/db.server.ts'
import { authSessionStorage } from '#app/utils/session.server.ts'
import { readSource } from '../sections/sections.server.tsx'
import { Description, Features, Source, type componentProps, Parts, Usage } from '../sections/sections.tsx'

export const handle = {
	breadcrumb: 'Flex',
}

const component: componentProps = {
	name: 'Flex',
	shortName: 'Flex',
	fileName: 'flex',
	category: 'layout',
	description: ['Component for creating unidimensional layouts.'],
	features: ['Inherits the space scale on the gap prop.', 'Removes inaccessible CSS features.'],
	parts: [
		{
			name: 'Flex',
			description: 'Component for creating unidimensional layouts.',
			props: [
				{ name: 'justify', type: "'start' | 'center' | 'end' | 'between'", default: "'start'", description: 'The alignment of items along the main axis.' },
				{ name: 'items', type: "'start' | 'center' | 'end' | 'stretch'", default: "'start'", description: 'The alignment of items along the cross axis.' },
				{ name: 'gap', type: '0 to 20', default: '0', description: 'The gap between items.' },
				{ name: 'orientation', type: "'horizontal' | 'vertical'", default: "'horizontal'", description: 'The orientation of the layout.' },
				{ name: 'inline', type: 'boolean', default: 'false', description: 'Whether the flex layout is inline or not.' },
				{ name: 'wrap', type: 'boolean', default: 'false', description: 'Whether items should wrap to multiple lines.' },
			],
		},
	],
	usage: `<Flex inline gap="6" className="mt-4 rounded-md bg-slate-200 p-4">
	<div className="h-10 w-10 rounded-sm bg-slate-600" />
	<div className="h-10 w-10 rounded-sm bg-slate-600" />
	<div className="h-10 w-10 rounded-sm bg-slate-600" />
	<div className="h-10 w-10 rounded-sm bg-slate-600" />
	<div className="h-10 w-10 rounded-sm bg-slate-600" />
</Flex>
<Flex gap="6" className="mt-4 rounded-md bg-slate-200 p-4">
	<div className="h-10 w-10 rounded-sm bg-slate-600" />
	<div className="h-10 w-10 rounded-sm bg-slate-600" />
	<div className="h-10 w-10 rounded-sm bg-slate-600" />
	<div className="h-10 w-10 rounded-sm bg-slate-600" />
	<div className="h-10 w-10 rounded-sm bg-slate-600" />
</Flex>
<Flex justify="center" gap="6" className="mt-4 rounded-md bg-slate-200 p-4">
	<div className="h-10 w-10 rounded-sm bg-slate-600" />
	<div className="h-10 w-10 rounded-sm bg-slate-600" />
	<div className="h-10 w-10 rounded-sm bg-slate-600" />
	<div className="h-10 w-10 rounded-sm bg-slate-600" />
	<div className="h-10 w-10 rounded-sm bg-slate-600" />
</Flex>
<Flex justify="end" gap="6" className="mt-4 rounded-md bg-slate-200 p-4">
	<div className="h-10 w-10 rounded-sm bg-slate-600" />
	<div className="h-10 w-10 rounded-sm bg-slate-600" />
	<div className="h-10 w-10 rounded-sm bg-slate-600" />
	<div className="h-10 w-10 rounded-sm bg-slate-600" />
	<div className="h-10 w-10 rounded-sm bg-slate-600" />
</Flex>
<Flex justify="between" gap="6" className="mt-4 rounded-md bg-slate-200 p-4">
	<div className="h-10 w-10 rounded-sm bg-slate-600" />
	<div className="h-10 w-10 rounded-sm bg-slate-600" />
	<div className="h-10 w-10 rounded-sm bg-slate-600" />
	<div className="h-10 w-10 rounded-sm bg-slate-600" />
	<div className="h-10 w-10 rounded-sm bg-slate-600" />
</Flex>
<Flex gap="6" className="mt-4 h-32 rounded-md bg-slate-200 p-4">
	<div className="h-10 w-10 rounded-sm bg-slate-600" />
	<div className="h-10 w-10 rounded-sm bg-slate-600" />
	<div className="h-10 w-10 rounded-sm bg-slate-600" />
	<div className="h-10 w-10 rounded-sm bg-slate-600" />
	<div className="h-10 w-10 rounded-sm bg-slate-600" />
</Flex>
<Flex items="center" gap="6" className="mt-4 h-32 rounded-md bg-slate-200 p-4">
	<div className="h-10 w-10 rounded-sm bg-slate-600" />
	<div className="h-10 w-10 rounded-sm bg-slate-600" />
	<div className="h-10 w-10 rounded-sm bg-slate-600" />
	<div className="h-10 w-10 rounded-sm bg-slate-600" />
	<div className="h-10 w-10 rounded-sm bg-slate-600" />
</Flex>
<Flex items="end" gap="6" className="mt-4 h-32 rounded-md bg-slate-200 p-4">
	<div className="h-10 w-10 rounded-sm bg-slate-600" />
	<div className="h-10 w-10 rounded-sm bg-slate-600" />
	<div className="h-10 w-10 rounded-sm bg-slate-600" />
	<div className="h-10 w-10 rounded-sm bg-slate-600" />
	<div className="h-10 w-10 rounded-sm bg-slate-600" />
</Flex>
<Flex items="stretch" gap="6" className="mt-4 h-32 rounded-md bg-slate-200 p-4">
	<div className="w-10 rounded-sm bg-slate-600" />
	<div className="w-10 rounded-sm bg-slate-600" />
	<div className="w-10 rounded-sm bg-slate-600" />
	<div className="w-10 rounded-sm bg-slate-600" />
	<div className="w-10 rounded-sm bg-slate-600" />
</Flex>`,
}

export const loader = async ({ request }: LoaderFunctionArgs) => {
	const authSession = await authSessionStorage.getSession(request.headers.get('cookie'))
	const sessionId = authSession.get(sessionKey)
	const session =
		sessionId ?
			await prisma.session.findUnique({
				select: { user: { select: { id: true, paid: true } } },
				where: { id: sessionId, expirationDate: { gt: new Date() } },
			})
		:	null
	const source = session?.user.paid ? await readSource({ category: component.category, fileName: component.fileName }) : null
	return json({ source })
}

const HeadingRoute = () => {
	const { source } = useLoaderData<typeof loader>()
	const { name, description, shortName, fileName, features, usage, parts } = component

	return (
		<>
			<Description name={name} description={description} />
			<Features features={features} />

			<H2>Accessibility</H2>
			<P>
				Changing visual order creates a disconnect between content and presentation and is, therefore,{' '}
				<a href="https://andrecasal.com/articles/the-dark-side-of-the-grid" target="_blank" rel="noreferrer" className="underline">
					bad for accessibility
				</a>
				. For this reason, the <Code>flex-direction: row-reverse</Code> and <Code>flex-direction: column-reverse</Code> options are omitted.
			</P>

			<Parts parts={parts} />

			<H2>Wrap</H2>
			<P>Flexbox loses a lot of its power if you make it wrap. If you need to wrap, you should probably use a grid instead.</P>

			<H2>Notes</H2>
			<P>
				If you set <Code>{'<Flex inline />'}</Code>, the div has the least width possible, so the <Code>justify</Code> prop has no meaning because the items won't have any extra
				horizontal space for the <Code>justify</Code> option to matter.
			</P>
			<P>
				If you set <Code>{'<Flex items="stretch" />'}</Code> and the children have a fixed height, <Code>{'items="stretch"'}</Code> will be ignored.
			</P>

			<Usage usage={usage} shortName={shortName} />
			<Flex inline gap="6" className="mt-4 rounded-md bg-slate-200 p-4">
				<div className="h-10 w-10 rounded-sm bg-slate-600" />
				<div className="h-10 w-10 rounded-sm bg-slate-600" />
				<div className="h-10 w-10 rounded-sm bg-slate-600" />
				<div className="h-10 w-10 rounded-sm bg-slate-600" />
				<div className="h-10 w-10 rounded-sm bg-slate-600" />
			</Flex>
			<Flex gap="6" className="mt-4 rounded-md bg-slate-200 p-4">
				<div className="h-10 w-10 rounded-sm bg-slate-600" />
				<div className="h-10 w-10 rounded-sm bg-slate-600" />
				<div className="h-10 w-10 rounded-sm bg-slate-600" />
				<div className="h-10 w-10 rounded-sm bg-slate-600" />
				<div className="h-10 w-10 rounded-sm bg-slate-600" />
			</Flex>
			<Flex justify="center" gap="6" className="mt-4 rounded-md bg-slate-200 p-4">
				<div className="h-10 w-10 rounded-sm bg-slate-600" />
				<div className="h-10 w-10 rounded-sm bg-slate-600" />
				<div className="h-10 w-10 rounded-sm bg-slate-600" />
				<div className="h-10 w-10 rounded-sm bg-slate-600" />
				<div className="h-10 w-10 rounded-sm bg-slate-600" />
			</Flex>
			<Flex justify="end" gap="6" className="mt-4 rounded-md bg-slate-200 p-4">
				<div className="h-10 w-10 rounded-sm bg-slate-600" />
				<div className="h-10 w-10 rounded-sm bg-slate-600" />
				<div className="h-10 w-10 rounded-sm bg-slate-600" />
				<div className="h-10 w-10 rounded-sm bg-slate-600" />
				<div className="h-10 w-10 rounded-sm bg-slate-600" />
			</Flex>
			<Flex justify="between" gap="6" className="mt-4 rounded-md bg-slate-200 p-4">
				<div className="h-10 w-10 rounded-sm bg-slate-600" />
				<div className="h-10 w-10 rounded-sm bg-slate-600" />
				<div className="h-10 w-10 rounded-sm bg-slate-600" />
				<div className="h-10 w-10 rounded-sm bg-slate-600" />
				<div className="h-10 w-10 rounded-sm bg-slate-600" />
			</Flex>
			<Flex gap="6" className="mt-4 h-32 rounded-md bg-slate-200 p-4">
				<div className="h-10 w-10 rounded-sm bg-slate-600" />
				<div className="h-10 w-10 rounded-sm bg-slate-600" />
				<div className="h-10 w-10 rounded-sm bg-slate-600" />
				<div className="h-10 w-10 rounded-sm bg-slate-600" />
				<div className="h-10 w-10 rounded-sm bg-slate-600" />
			</Flex>
			<Flex items="center" gap="6" className="mt-4 h-32 rounded-md bg-slate-200 p-4">
				<div className="h-10 w-10 rounded-sm bg-slate-600" />
				<div className="h-10 w-10 rounded-sm bg-slate-600" />
				<div className="h-10 w-10 rounded-sm bg-slate-600" />
				<div className="h-10 w-10 rounded-sm bg-slate-600" />
				<div className="h-10 w-10 rounded-sm bg-slate-600" />
			</Flex>
			<Flex items="end" gap="6" className="mt-4 h-32 rounded-md bg-slate-200 p-4">
				<div className="h-10 w-10 rounded-sm bg-slate-600" />
				<div className="h-10 w-10 rounded-sm bg-slate-600" />
				<div className="h-10 w-10 rounded-sm bg-slate-600" />
				<div className="h-10 w-10 rounded-sm bg-slate-600" />
				<div className="h-10 w-10 rounded-sm bg-slate-600" />
			</Flex>
			<Flex items="stretch" gap="6" className="mt-4 h-32 rounded-md bg-slate-200 p-4">
				<div className="w-10 rounded-sm bg-slate-600" />
				<div className="w-10 rounded-sm bg-slate-600" />
				<div className="w-10 rounded-sm bg-slate-600" />
				<div className="w-10 rounded-sm bg-slate-600" />
				<div className="w-10 rounded-sm bg-slate-600" />
			</Flex>

			<Source source={source} fileName={fileName} />
		</>
	)
}
export default HeadingRoute
